import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";

// Wedding target: 19 May 2027, 16:00 local Cyprus time (EEST, UTC+3).
// Using a fixed UTC timestamp so the countdown is identical for every visitor,
// regardless of the visitor's timezone.
const WEDDING_UTC = Date.UTC(2027, 4, 19, 13, 0, 0); // 13:00 UTC == 16:00 EEST
// Consider the wedding day itself (from wedding start until +24h) as a distinct
// "Today. Yamas!" phase. After that, we flip to counting years of marriage.
const WEDDING_DAY_END = WEDDING_UTC + 24 * 60 * 60 * 1000;

const pad = (n) => String(n).padStart(2, "0");

const computeParts = (diffMs) => {
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

/** After the wedding day, how many full days/months/years since. */
const computeAnniversary = (now) => {
  const totalSeconds = Math.max(0, Math.floor((now - WEDDING_UTC) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const months = Math.floor(days / 30.4375); // avg month
  const years = Math.floor(days / 365.25);
  return { days, months, years };
};

export const CountdownTimer = ({ variant = "hero", showOverline = true }) => {
  const { t } = useLang();
  const [parts, setParts] = useState(() => computeParts(WEDDING_UTC - Date.now()));
  const [phase, setPhase] = useState(() => {
    const now = Date.now();
    if (now < WEDDING_UTC) return "counting";
    if (now < WEDDING_DAY_END) return "today";
    return "after";
  });
  const [anniv, setAnniv] = useState(() => computeAnniversary(Date.now()));

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const diff = WEDDING_UTC - now;
      if (diff > 0) {
        setPhase("counting");
        setParts(computeParts(diff));
        return;
      }
      if (now < WEDDING_DAY_END) {
        setPhase("today");
        return;
      }
      setPhase("after");
      setAnniv(computeAnniversary(now));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const isHero = variant === "hero";

  const overlineClass = isHero
    ? "uppercase font-medium text-[11px] sm:text-[12px] tracking-[0.24em] text-white/85"
    : "editorial-kicker";
  const overlineStyle = isHero
    ? { textShadow: "0 2px 10px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.7)" }
    : undefined;

  if (phase === "today") {
    return (
      <div
        className={isHero ? "text-white" : ""}
        data-testid="countdown-reached"
      >
        {showOverline && (
          <p className={overlineClass} style={overlineStyle}>
            {t("home.countdownOverline")}
          </p>
        )}
        <p
          className="font-serif-display italic text-2xl sm:text-3xl mt-3"
          style={
            isHero
              ? { textShadow: "0 2px 10px rgba(0,0,0,0.85)" }
              : undefined
          }
        >
          {t("home.countdownStarted")}
        </p>
      </div>
    );
  }

  if (phase === "after") {
    // Prefer the biggest sensible unit: years > months > days.
    let bigNumber;
    let smallLabel;
    if (anniv.years >= 1) {
      bigNumber = anniv.years;
      smallLabel = anniv.years === 1 ? t("home.countdownYearOfUs") : t("home.countdownYearsOfUs");
    } else if (anniv.months >= 1) {
      bigNumber = anniv.months;
      smallLabel = anniv.months === 1 ? t("home.countdownMonthOfUs") : t("home.countdownMonthsOfUs");
    } else {
      bigNumber = Math.max(1, anniv.days);
      smallLabel = anniv.days <= 1 ? t("home.countdownDayOfUs") : t("home.countdownDaysOfUs");
    }
    return (
      <div
        className={isHero ? "text-white" : ""}
        data-testid="countdown-anniversary"
      >
        {showOverline && (
          <p className={overlineClass} style={overlineStyle}>
            {t("home.countdownAnnivOverline")}
          </p>
        )}
        <div className="mt-4 flex items-baseline gap-3 sm:gap-4">
          <span
            className={
              (isHero ? "text-white " : "text-foreground ") +
              "font-serif-display leading-none tabular-nums tracking-tight text-[64px] sm:text-[88px] lg:text-[112px]"
            }
            style={
              isHero
                ? { textShadow: "0 2px 12px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.7)" }
                : undefined
            }
            data-testid="countdown-anniv-value"
          >
            {bigNumber}
          </span>
          <span
            className={
              (isHero ? "text-white/90 " : "text-foreground/70 ") +
              "font-serif-display italic text-xl sm:text-2xl lg:text-3xl"
            }
            style={
              isHero
                ? { textShadow: "0 2px 10px rgba(0,0,0,0.85)" }
                : undefined
            }
          >
            {smallLabel}
          </span>
        </div>
      </div>
    );
  }

  const units = [
    { key: "d", label: t("home.countdownDays"), value: parts.days, digits: 3 },
    { key: "h", label: t("home.countdownHours"), value: parts.hours, digits: 2 },
    { key: "m", label: t("home.countdownMinutes"), value: parts.minutes, digits: 2 },
    { key: "s", label: t("home.countdownSeconds"), value: parts.seconds, digits: 2 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
      className={isHero ? "text-white" : ""}
      data-testid="countdown-timer"
      role="timer"
      aria-live="polite"
      aria-label={`${t("home.countdownOverline")} — ${parts.days} ${t("home.countdownDays")}, ${parts.hours} ${t("home.countdownHours")}, ${parts.minutes} ${t("home.countdownMinutes")}, ${parts.seconds} ${t("home.countdownSeconds")}`}
    >
      {showOverline && (
        <p
          className={overlineClass}
          style={overlineStyle}
          data-testid="countdown-overline"
        >
          {t("home.countdownOverline")}
        </p>
      )}

      <div
        className={`${showOverline ? "mt-4 sm:mt-5" : ""} grid grid-cols-4 gap-2 sm:gap-4 max-w-[520px]`}
        data-testid="countdown-units"
      >
        {units.map((u) => (
          <div
            key={u.key}
            className={
              (isHero
                ? "border-t border-white/30 pt-3 sm:pt-4 "
                : "border-t border-foreground/20 pt-3 sm:pt-4 ") +
              "flex flex-col items-start"
            }
            data-testid={`countdown-unit-${u.key}`}
          >
            <span
              className={
                (isHero ? "text-white " : "text-foreground ") +
                "font-serif-display leading-none tabular-nums tracking-tight text-[34px] sm:text-[48px] lg:text-[56px]"
              }
              style={
                isHero
                  ? { textShadow: "0 2px 12px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.7)" }
                  : undefined
              }
              data-testid={`countdown-value-${u.key}`}
            >
              {u.digits === 3 ? String(u.value).padStart(3, "0") : pad(u.value)}
            </span>
            <span
              className={
                (isHero ? "text-white/85 " : "text-foreground/60 ") +
                "mt-2 uppercase font-medium text-[10px] sm:text-[11px] tracking-[0.24em]"
              }
              style={
                isHero
                  ? { textShadow: "0 1px 8px rgba(0,0,0,0.85)" }
                  : undefined
              }
            >
              {u.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default CountdownTimer;
